import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { BellIcon, ClipboardDocumentListIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { foraDaProva } from '@/features/relatos/podeRelatar';
import { useContagemDeNaoLidas } from '@/features/notificacoes/api';

/**
 * A NAVEGAÇÃO DO ALUNO NO CELULAR (23/10/2026, revisão do pesquisador): uma barra fixa embaixo com três abas — Simulados,
 * Notificações (com o número) e Conta —, no app e na web abaixo de 768 px, no lugar da gaveta que tinha um item só. No
 * computador, como estava. NUNCA dentro da prova (`foraDaProva`, a mesma regra do sino e do "Relatar algo"). O espaço que
 * ela ocupa no fim da página é a classe `com-abas-embaixo` no body (index.css).
 */
const ABAS = [
  { para: '/student', rotulo: 'Simulados', Icone: ClipboardDocumentListIcon, fim: true },
  { para: '/notificacoes', rotulo: 'Notificações', Icone: BellIcon, fim: false },
  { para: '/conta', rotulo: 'Conta', Icone: UserCircleIcon, fim: false },
];

export function useMostraAbasDoAluno(): boolean {
  const { user } = useAuth();
  const { pathname } = useLocation();
  return user?.role === 'STUDENT' && foraDaProva(pathname);
}

export function AbasDoAluno() {
  const mostra = useMostraAbasDoAluno();
  const { pathname } = useLocation();
  const { data: naoLidas = 0 } = useContagemDeNaoLidas(mostra, pathname);
  useEffect(() => {
    if (!mostra) return;
    document.body.classList.add('com-abas-embaixo');
    return () => document.body.classList.remove('com-abas-embaixo');
  }, [mostra]);
  if (!mostra) return null;
  return (
    <nav aria-label="Navegação do aluno" data-testid="abas-do-aluno"
      className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-3 border-t border-gray-200 bg-white pb-[env(safe-area-inset-bottom)] md:hidden">
      {ABAS.map(({ para, rotulo, Icone, fim }) => (
        <NavLink key={para} to={para} end={fim} data-testid={`aba-${para.replace(/\W/g, '') || 'inicio'}`}
          className={({ isActive }) => `relative flex min-h-[56px] flex-col items-center justify-center gap-0.5 text-xs ${isActive ? 'font-semibold text-marca-700' : 'text-gray-600'}`}>
          <Icone className="h-6 w-6" aria-hidden />
          {rotulo}
          {para === '/notificacoes' && naoLidas > 0 && (
            <span className="absolute left-1/2 top-1.5 ml-2 min-w-[18px] rounded-full bg-red-600 px-1 text-center text-[11px] font-semibold leading-[18px] text-white" data-testid="numero-de-notificacoes">
              {naoLidas > 99 ? '99+' : naoLidas}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
